import { api, type Page } from './client'

/**
 * F-043 (REQ-267-001) — client for the Notifications module's own surface, built by F-042/
 * TASK-0296 (`NotificationsController.cs`/`NotificationPreferencesController.cs`, backend, already
 * on `main`). Every shape below mirrors the backend's real DTOs field-for-field — read directly
 * from the controllers before writing this file, nothing here is guessed.
 */

/** Mirrors `EqualCrm.BuildingBlocks.Notifications.NotificationCategory` — serialized as the member
 * name (`JsonStringEnumConverter`), verbatim from REQ-267-001's own four category names. */
export type NotificationCategory = 'Critica' | 'RichiedeAzione' | 'Informativa' | 'Successo'

/** Mirrors `EqualCrm.Modules.Notifications.Domain.NotificationChannel`. Only `'Internal'` has any
 * real row today — `'Email'` is reserved on the backend (see that enum's own remarks) and never
 * appears in this frontend. */
export type NotificationChannel = 'Internal' | 'Email' | 'Push'

/** One row of `NotificationsController.List`'s response — mirrors `NotificationRow` exactly. */
export interface NotificationRow {
  id: number
  category: NotificationCategory
  eventType: string
  title: string
  body: string
  referenceType: string | null
  referenceId: number | null
  createdAt: string
  readAt: string | null
  archivedAt: string | null
  /** F-087: id della notifica "Riepilogo" che ha raccolto questa riga (altrimenti null). */
  digestId?: number | null
}

export interface NotificationListFilters {
  category?: NotificationCategory
  unreadOnly?: boolean
  includeArchived?: boolean
  /** F-087: solo le notifiche raccolte da questo riepilogo. */
  digestId?: number
  page?: number
  size?: number
}

/** Dispatched on `window` after any mutation below (mark read/read-all/archive/preference change)
 * so anything rendered outside this request's own tree — today only `NotificationBell` in the
 * topbar — can refresh itself immediately instead of waiting for its own poll interval. Plain
 * `Event`, not `CustomEvent`: no listener needs a payload, "something changed, re-fetch your own
 * count" is the entire contract. */
export const NOTIFICATIONS_CHANGED_EVENT = 'ecrm:notifications-changed'

function notifyChanged(): void {
  window.dispatchEvent(new Event(NOTIFICATIONS_CHANGED_EVENT))
}

export const notificationsApi = {
  /** `GET /api/notifications` — the caller's own notifications, newest first. */
  list: (filters: NotificationListFilters = {}) => {
    const params = new URLSearchParams()
    if (filters.category) params.set('category', filters.category)
    if (filters.unreadOnly) params.set('unreadOnly', 'true')
    if (filters.includeArchived) params.set('includeArchived', 'true')
    if (filters.digestId) params.set('digestId', String(filters.digestId))
    params.set('page', String(filters.page ?? 1))
    params.set('size', String(filters.size ?? 20))
    return api<Page<NotificationRow>>(`/api/notifications?${params.toString()}`)
  },

  /** `GET /api/notifications/unread-count`. */
  unreadCount: () => api<{ count: number }>('/api/notifications/unread-count'),

  /** `POST /api/notifications/{id}/read` — 204, 404 if the id doesn't exist or belongs to someone
   * else. Idempotent on the backend (re-marking a read notification is a no-op, `ReadAt` never
   * slides forward). */
  markRead: (id: number) =>
    api<void>(`/api/notifications/${id}/read`, { method: 'POST' }).then(r => { notifyChanged(); return r }),

  /** `POST /api/notifications/read-all` — marks every currently-unread notification of the caller
   * read in one call, returns how many were touched. */
  markAllRead: () =>
    api<{ updated: number }>('/api/notifications/read-all', { method: 'POST' }).then(r => { notifyChanged(); return r }),

  /** `POST /api/notifications/{id}/archive` — 204, 404 same as `markRead`. There is no
   * "unarchive": once archived, a row stays out of the default/unread views for good (backend
   * remarks on `Notification.ArchivedAt`) — the caller UI must make that clear before calling this. */
  archive: (id: number) =>
    api<void>(`/api/notifications/${id}/archive`, { method: 'POST' }).then(r => { notifyChanged(); return r }),
}

/** Mirrors `NotificationPreferenceRow` (`NotificationPreferencesController.cs`) — only the rows the
 * caller has explicitly set. An event type absent here defaults to enabled (backend's own
 * documented default, `NotificationPreference`'s remarks) — never render a missing row as "off". */
export interface NotificationPreferenceRow {
  eventType: string
  channel: NotificationChannel
  enabled: boolean
  updatedAt: string
  /** F-087: modo di consegna. Assente nelle risposte vecchie: vale Immediata/Disattivata da `enabled`. */
  mode?: DeliveryMode
}

/** F-087 (REQ-268-001, REQ-269-001): quando arriva una notifica interna. L'unico canale e' il centro
 * notifiche; l'email non e' disponibile (SMTP non configurato). Le notifiche Critiche sono sempre immediate. */
export type DeliveryMode = 'Immediata' | 'Riepilogo' | 'Disattivata'

export const DELIVERY_MODE_LABELS: Record<DeliveryMode, string> = {
  Immediata: 'Subito',
  Riepilogo: 'Nel riepilogo',
  Disattivata: 'Disattivata',
}

export const notificationPreferencesApi = {
  /** `GET /api/notification-preferences` — every preference the caller has explicitly set, across
   * every channel. Not paginated (a plain array): the backend has no notion of a catalog to page
   * through, see that controller's own remarks. */
  list: () => api<NotificationPreferenceRow[]>('/api/notification-preferences'),

  /** `PUT /api/notification-preferences/{eventType}` — upserts the caller's own preference for
   * `eventType` on the internal channel (the only one this task builds; the backend's default
   * channel already is `Internal` when omitted, sent explicitly here for clarity). 204 on success. */
  setEnabled: (eventType: string, enabled: boolean) =>
    api<void>(`/api/notification-preferences/${encodeURIComponent(eventType)}`, {
      method: 'PUT',
      body: JSON.stringify({ enabled, channel: 'Internal' satisfies NotificationChannel }),
    }).then(r => { notifyChanged(); return r }),

  /** Notifiche sul telefono per un evento (canale Push): acceso o spento. 204. */
  setPush: (eventType: string, enabled: boolean) =>
    api<void>(`/api/notification-preferences/${encodeURIComponent(eventType)}`, {
      method: 'PUT',
      body: JSON.stringify({ mode: enabled ? 'Immediata' : 'Disattivata', channel: 'Push' satisfies NotificationChannel }),
    }),

  /** F-087: `PUT` con il modo di consegna (Immediata/Riepilogo/Disattivata). 204. */
  setMode: (eventType: string, mode: DeliveryMode) =>
    api<void>(`/api/notification-preferences/${encodeURIComponent(eventType)}`, {
      method: 'PUT',
      body: JSON.stringify({ mode, channel: 'Internal' satisfies NotificationChannel }),
    }).then(r => { notifyChanged(); return r }),
}

/** F-087 (REQ-269-001): l'orario del proprio riepilogo, in ora di Roma. */
export interface DigestSchedule {
  isEnabled: boolean
  localTime: string | null
  /** Giorni ISO: 1 = lunedi' ... 7 = domenica. */
  weekdays: number[]
  configured: boolean
  /** Solo un suggerimento: nessun orario e' attivo finche' l'utente non lo salva. */
  suggestedTime: string
}

export const notificationDigestApi = {
  get: () => api<DigestSchedule>('/api/notification-digest'),
  save: (body: { isEnabled: boolean; localTime: string; weekdays: number[] }) =>
    api<void>('/api/notification-digest', { method: 'PUT', body: JSON.stringify(body) }),
}

/** F-087 (REQ-268-001): impostazioni Admin per tipo di evento ("Canali per evento"). */
export interface NotificationEventSetting {
  eventType: string
  label: string
  defaultMode: DeliveryMode
  allowDigest: boolean
  configured: boolean
  seenInData: boolean
  /** R3-16: l'evento parte anche per email (solo con SMTP configurato). */
  emailEnabled: boolean
}

/** R5-05 (S1): stato e test di connessione del server SMTP (solo Admin; nessuna password in risposta). */
export interface EmailTransportState {
  settings: { configured: boolean; host: string | null; port: number; security: string; hasCredentials: boolean; fromAddress: string; fromName: string }
  sendingEnabled: boolean
}

export const emailTransportApi = {
  get: () => api<EmailTransportState>('/api/email-transport'),
  test: () => api<{ ok: boolean; error: string | null }>('/api/email-transport/test', { method: 'POST' }),
}

export const notificationEventSettingsApi = {
  list: () => api<{
    emailChannel: { available: boolean; reason: string }
    items: NotificationEventSetting[]
  }>('/api/notification-event-settings'),
  save: (eventType: string, body: { label: string; defaultMode: DeliveryMode; allowDigest: boolean; emailEnabled?: boolean }) =>
    api<void>(`/api/notification-event-settings/${encodeURIComponent(eventType)}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),
  reset: (eventType: string) =>
    api<void>(`/api/notification-event-settings/${encodeURIComponent(eventType)}`, { method: 'DELETE' }),
}

/** F-087 (REQ-084-002, REQ-085-001): regole Admin dei promemoria interni degli appuntamenti. */
export type ReminderRecipient = 'Assegnatario' | 'Creatore' | 'Entrambi'

export interface AppointmentReminderPolicy {
  id: number
  name: string
  campaignId: number | null
  appointmentType: string | null
  minutesBefore: number
  recipient: ReminderRecipient
  isActive: boolean
  updatedAt: string
}

export interface AppointmentReminderPolicyForm {
  name: string
  campaignId: number | null
  appointmentType: string | null
  minutesBefore: number
  recipient: ReminderRecipient
  isActive: boolean
}

export const appointmentReminderPoliciesApi = {
  list: () => api<{ suggestedMinutesBefore: number; items: AppointmentReminderPolicy[] }>('/api/appointment-reminder-policies'),
  create: (body: AppointmentReminderPolicyForm) =>
    api<{ id: number }>('/api/appointment-reminder-policies', { method: 'POST', body: JSON.stringify(body) }),
  update: (id: number, body: AppointmentReminderPolicyForm) =>
    api<void>(`/api/appointment-reminder-policies/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  remove: (id: number) => api<void>(`/api/appointment-reminder-policies/${id}`, { method: 'DELETE' }),
}

/**
 * The event-type catalog REQ-025-004's UI needs — deliberately NOT served by the backend (see
 * `NotificationPreferencesController`'s own remarks: the Notifications module has zero
 * `ProjectReference` on any producer module, so it cannot enumerate "every event type that
 * exists" without breaking that boundary). Read directly out of the ONE producer that exists today,
 * `ContractLifecycleNotificationRules.cs` (Contracts module) — six constants, six rules, verified
 * against that file's own `EventType` values and `BuildTitle` wording, not invented.
 *
 * A future second producer (e.g. a Leads-module event) would add its own entries here — this list
 * is a frontend-side catalog by design, same reasoning the backend's own remarks describe for why
 * it isn't sent over the wire.
 */
export interface NotificationEventTypeCatalogEntry {
  eventType: string
  label: string
}

export const NOTIFICATION_EVENT_TYPE_CATALOG: readonly NotificationEventTypeCatalogEntry[] = [
  // Pratiche: ogni cambio di stato (flag notifiche.cambi_stato, `ContractStatusChangeMessages` sul server).
  { eventType: 'contract.nuova', label: 'Nuova pratica a tuo nome (creata da altri o importata dal portale)' },
  { eventType: 'contract.status.bozza', label: 'Pratica tornata in bozza' },
  { eventType: 'contract.status.in_attesa_di_firma', label: 'Pratica in attesa della firma del cliente' },
  { eventType: 'contract.status.inviata_al_backoffice', label: 'Pratica inviata al backoffice' },
  { eventType: 'contract.status.presa_in_carico_backoffice', label: 'Pratica presa in carico dal backoffice' },
  { eventType: 'contract.status.integrazione_richiesta', label: 'Documenti o dati da integrare' },
  { eventType: 'contract.status.verificata', label: 'Pratica verificata' },
  { eventType: 'contract.status.caricata_a_lotto', label: 'Pratica caricata a lotto' },
  { eventType: 'contract.status.approvazione_richiesta', label: 'Lotto inviato per approvazione' },
  { eventType: 'contract.status.lotto_approvato', label: 'Lotto della pratica approvato' },
  { eventType: 'contract.status.caricamento_operatore', label: "Pratica in caricamento sul portale dell'operatore" },
  { eventType: 'contract.status.da_confermare', label: 'Pratica da confermare sul portale' },
  { eventType: 'contract.status.inviata_all_operatore', label: "Pratica inviata all'operatore" },
  { eventType: 'contract.status.presa_in_carico_operatore', label: "Pratica in lavorazione presso l'operatore" },
  { eventType: 'contract.status.attivata', label: 'Pratica attivata' },
  { eventType: 'contract.status.rifiutata', label: 'Pratica rifiutata (KO)' },
  { eventType: 'contract.status.annullata', label: 'Pratica annullata' },
  { eventType: 'contract.status.sospesa', label: 'Pratica sospesa' },
  { eventType: 'contract.status.stornata', label: 'Pratica stornata' },
  // Portali.
  { eventType: 'coraly.update', label: 'Aggiornamento dal portale (Coraly)' },
  { eventType: 'contract.ermes.firmato', label: 'Contratto firmato su Ermes' },
  { eventType: 'contract.portale.problema', label: 'Problema segnalato dal portale (amministratori)' },
  { eventType: 'contract.sospensione.risolta', label: 'Sospensione risolta (amministrazione e backoffice)' },
  // F-078: comunicazioni interne sul lotto di scarico (ai Manager e al BackOffice).
  { eventType: 'batch.submitted_for_approval', label: 'Lotto inviato per approvazione (manager)' },
  { eventType: 'batch.discharge_approved', label: 'Scarico del lotto approvato' },
  // Lead e appuntamenti.
  { eventType: 'lead.escalation.agent', label: 'Lead da richiamare (sollecito)' },
  { eventType: 'lead.escalation.team_leader', label: 'Lead non gestito da un agente del team' },
  { eventType: 'lead.recycled', label: 'Lead riassegnato' },
  { eventType: 'appointment.reminder', label: 'Promemoria appuntamento' },
  { eventType: 'appointment.no_show', label: "Cliente non presentato all'appuntamento" },
  // Sistema.
  { eventType: 'integration.retries_exhausted', label: 'Integrazione in errore dopo i ritentativi' },
  { eventType: 'automation.rule', label: 'Regole automatiche' },
  { eventType: 'notification.digest', label: 'Riepilogo delle notifiche' },
]

/** A human label for `eventType`, falling back to the raw technical key for anything not in the
 * catalog above (a future producer this frontend hasn't been taught about yet — better an ugly but
 * honest label than a silently dropped row). */
export function eventTypeLabel(eventType: string): string {
  return NOTIFICATION_EVENT_TYPE_CATALOG.find(e => e.eventType === eventType)?.label ?? eventType
}
